import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from '@/hooks/useToast';
import {
  AllpayUserDTO,
  getListAllpayUsersQueryKey,
  TravellerWithCollectionMembershipsDTO,
  useUpdateAllpayUserCreditLimit,
} from '@/api/trafikportalen';
import Button from '../core/Button';
import { IconCheck } from '@/icons';
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Input from '../core/Input';
import { FormElement } from '../FormElement';
import { OrganizationCreditInfo } from '../OrganizationCreditInfo';
import { useOrgCreditInfo } from '@/hooks/useOrgCreditInfo';
import { TravellerCreditInfo } from '../TravellerCreditInfo';

interface Props {
  allpayUser: AllpayUserDTO;
  traveller: TravellerWithCollectionMembershipsDTO;
  onOpenChange: (open: boolean) => void;
}

export const AllpayUserUpdateForm = ({
  allpayUser,
  traveller,
  onOpenChange,
}: Props) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const updateAllpayUser = useUpdateAllpayUserCreditLimit();
  const { remainingCredit, hasNoCreditLimit } = useOrgCreditInfo();

  const [newCredit, setNewCredit] = useState(allpayUser.credit.toString());

  const creditError = useMemo(() => {
    if (!newCredit) {
      return '';
    }
    const credit = parseInt(newCredit);
    if (isNaN(credit)) {
      return formatMessage({ defaultMessage: 'Ogiltigt värde' });
    }
    if (credit < 0) {
      return formatMessage({
        defaultMessage: 'Kreditgränsen kan inte vara negativ',
      });
    }
    if (credit > 20000) {
      return formatMessage({
        defaultMessage: 'Kreditgränsen kan inte överstiga 20 000 SEK',
      });
    }
    if (!hasNoCreditLimit && credit > remainingCredit) {
      return formatMessage({
        defaultMessage:
          'Kreditgränsen kan inte överstiga organisationens kvarvarande kredit',
      });
    }
    return '';
  }, [newCredit, remainingCredit, formatMessage, hasNoCreditLimit]);

  const handleUpdate = useCallback(() => {
    updateAllpayUser.mutate(
      {
        id: allpayUser.id,
        data: {
          credit: Number(newCredit),
        },
      },
      {
        onSuccess: async () => {
          toast.success(
            {
              title: formatMessage({
                defaultMessage: `Kreditgräns uppdaterad`,
              }),
            },
            { autoClose: 3000 },
          );
          await queryClient.invalidateQueries({
            queryKey: getListAllpayUsersQueryKey(),
          });
          onOpenChange(false);
        },
        onError: (error) => {
          console.error('Error', error);
          toast.error(
            {
              title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
              description: error.message,
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  }, [
    updateAllpayUser,
    allpayUser,
    newCredit,
    formatMessage,
    queryClient,
    onOpenChange,
  ]);

  return (
    <div className="flex flex-col gap-5">
      <OrganizationCreditInfo includeTitle={false} />
      <TravellerCreditInfo traveller={traveller} />
      <FormElement
        label={<FormattedMessage defaultMessage="Ny kreditgräns" />}
        input={
          <Input
            id="credit"
            type="text"
            value={newCredit}
            onChange={(e) => setNewCredit(e.target.value)}
            endAdornment={<FormattedMessage defaultMessage="SEK" />}
            error={creditError}
          />
        }
      />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
        <Button
          type="submit"
          variant="filled"
          startIcon={<IconCheck />}
          onClick={handleUpdate}
          disabled={!!creditError}
          loading={updateAllpayUser.isPending}
        >
          <FormattedMessage defaultMessage="Uppdatera" />
        </Button>
      </div>
    </div>
  );
};
